import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl, tablet } from '../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, HideScrollbar, LineClamp } from '../../global/commonStyle';

interface Props {
  isDisabled?: boolean;
  isCurrent?: boolean;
  isEmptyState?: boolean;
}
const TimeTrackingDiv = styled.div`
  max-width: 1022px;
  margin: auto;
  height: calc(100dvh - 50px);
  position: relative;
  padding: var(--space-4) var(--space-24) var(--space-0);
  ${HideScrollbar}
  ${mobile_xl(css`
    padding: var(--space-0) var(--space-20);
    height: 100dvh;
  `)}
`;
const HeaderBlock = styled.div`
  max-width: 1022px;
  margin: auto;
  position: sticky;
  top: 0;
  ${FlexBetween}
  padding: var(--space-10) var(--space-24);
  gap: var(--space-80);
  z-index: var(--z-index-header);
  background-color: var(--background-secondary);
`;

const Heading = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;
const Icon = styled.div`
  display: inline-flex;
  .time-header-icon {
    fill: var(--brand-primary);
  }
  .close-icon {
    cursor: pointer;
    stroke: var(--text-primary);
    &:hover {
      background-color: var(--neutral-secondary);
      border-radius: var(--border-radius-4);
    }
  }
`;

const Title = styled.h1`
  ${Typography.heading_md}
  color: var(--text-primary);
  margin: var(--space-0);
`;

const DateSlider = styled.div`
  width: 100%;
  ${AlignCenter}
  background: var(--background-primary);
  border-radius: var(--border-radius-12);
  border: 0.5px solid var(--border-primary);
  .left-arrow-icon {
    height: 66px;
    border-radius: var(--border-radius-12) var(--border-radius-0) var(--border-radius-0) var(--border-radius-12);
    ${mobile_xl(css`
      height: 70px;
    `)}
  }
  .right-arrow-icon {
    height: 66px;
    border-radius: var(--border-radius-0) var(--border-radius-12) var(--border-radius-12) var(--border-radius-0);
    ${mobile_xl(css`
      height: 70px;
    `)}
  }
`;

const SliderBox = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  align-items: flex-start;
  padding: var(--space-10) var(--space-16);
  border-top: 2px solid transparent;
  cursor: pointer;
  max-width: 106px;
  min-width: 106px;
  width: 100%;
  border-right: 0.5px solid var(--border-primary);
  &:last-child {
    border-right: 0;
  }
  ${(props: Props) =>
    props.isDisabled &&
    css`
      color: var(--border-primary);
      opacity: 0.8;
      pointer-events: none;
      background-color: var(--background-secondary);
    `}

  ${(props: Props) =>
    props.isCurrent &&
    css`
      background: var(--neutral-secondary);
      border-top: 2px solid var(--brand-primary);
    `}

    ${(props: Props) =>
    !props.isDisabled &&
    css`
      &:hover {
        background: var(--neutral-secondary);
        border-top: 2px solid var(--brand-primary);
      }
    `}
    ${mobile_xl(css`
    gap: var(--space-6);
    :last-child {
      border-right: 0;
    }
  `)}
  ${mobile_xl(css`
    min-width: unset;
    max-width: 145px;
  `)}
`;

const DateTitle = styled.h1<Props>`
  ${Typography.body_sm_medium}
  color: var(--text-secondary);
  margin: var(--space-0);
  ${mobile_xl(css`
    width: 118px;
  `)}
  ${(props: Props) =>
    props.isDisabled &&
    css`
      color: var(--border-primary);
    `}
`;

const TImeSection = styled.div`
  ${AlignCenter}
  gap: var(--space-11);
  ${mobile_xl(css`
    gap: var(--space-8);
  `)}
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: var(--border-radius-full);
  background-color: var(--text-tertiary);
`;

const TimeTitle = styled.p<Props>`
  ${Typography.body_lg}
  margin: var(--space-0);
  color: var(--text-primary);

  ${(props: Props) =>
    props.isDisabled &&
    css`
      color: var(--border-primary);
    `}
`;

const MainContent = styled.div`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  background: var(--background-primary);
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-modal);
`;
const TopicData = styled.div`
  display: flex;
  justify-content: space-between;
  padding: var(--space-12) var(--space-16);
  gap: var(--space-24);
  border-bottom: 0.5px solid var(--border-primary);
  :last-child {
    border: none;
  }
  ${mobile_xl(css`
    gap: var(--space-12);
    padding: var(--space-12);
  `)}
`;
const LeftContent = styled.div`
  width: calc(100% - 350px);
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  ${tablet(css`
    width: max-content;
  `)}
  ${mobile_xl(css`
    gap: var(--space-6);
  `)}
`;
const RightContent = styled.div`
  ${AlignCenter}
  gap: var(--space-20);
`;
const TopicDescription = styled.p`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  ${LineClamp(2)}
  word-break: break-all;
  ${mobile_xl(css`
    width: 100%;
    ${LineClamp(1)}
    color: var(--text-secondary);
  `)}
`;
const TopicTitle = styled.h2`
  ${Typography.body_lg_medium}
  color: var(--text-primary);
  margin: var(--space-0);
  ${mobile_xl(css`
    ${LineClamp(1)}
    width: 100%;
    -
  `)}
`;
const TimeStop = styled.div`
  margin-right: var(--space-8);
  padding-right: var(--space-12);
  border-right: 0.5px solid var(--border-primary);
  cursor: pointer;
  width: 24px;
  height: 24px;
  .time-stop-icon {
    fill: var(--accent-success);
  }
  ${mobile_xl(css`
    padding-right: var(--space-6);
    margin-right: var(--space-6);
  `)}
`;

const TrackingContent = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  padding-bottom: var(--space-24);
  ${mobile_xl(css`
    padding-block: var(--space-8) var(--space-96);
    gap: var(--space-16);
  `)}
  ${({ isEmptyState }) =>
    isEmptyState &&
    css`
      gap: var(--space-0);
      padding-bottom: var(--space-0);
      ${mobile_xl(css`
        gap: var(--space-0);
      `)}
    `}
`;

const MiddleLine = styled.div`
  display: flex;
  width: 100%;
  border-inline: 0.5px solid var(--border-primary);
  ${HideScrollbar}
`;

const Header = styled.div`
  ${AlignCenter}
  gap: var(--space-24);
  ${mobile_xl(css`
    gap: var(--space-12);
  `)}
`;

const Empty = styled.div`
  position: relative;
  width: 100%;
  height: calc(100dvh - 119px);
  ${mobile_xl(css`
    height: calc(100dvh - 255px);
  `)}
`;

const RightSideIcon = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  ${mobile_xl(css`
    gap: var(--space-18);
  `)}
`;
const InvoiceLocked = styled.div`
  padding-left: var(--space-8);
  margin-left: var(--space-8);
  border-left: 0.5px solid var(--border-primary);
  .lock-icon:hover {
    background-color: unset;
  }
`;
const IconSection = styled.div`
  ${AlignCenter}
  .active-dropdown {
    background-color: var(--neutral-secondary);
    &:hover {
      background-color: var(--neutral-secondary);
    }
    border-radius: var(--border-radius-6);
  }
`;
export {
  HeaderBlock,
  LeftContent,
  RightContent,
  Heading,
  Icon,
  Title,
  DateTitle,
  DateSlider,
  TImeSection,
  TimeTitle,
  SliderBox,
  TimeTrackingDiv,
  MainContent,
  TopicData,
  TopicTitle,
  TopicDescription,
  TimeStop,
  TrackingContent,
  MiddleLine,
  Header,
  Empty,
  RightSideIcon,
  InvoiceLocked,
  IconSection,
  Dot
};
